// src/features/resetCredentials/useResetCredentials.js
import { useState } from "react";

/**
 * フルURL固定版（.env不要）
 * - 常に FULL_ENDPOINT_URL を使う
 * - まず POST { email } を試し、405/404/410 の場合は GET ?email=... にフォールバック
 * - 410（MEMBERSHIP_ENDED）はバックエンドの意図どおりにそのまま返す（フロントで分岐）
 */
export default function useResetCredentials() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null); // ← 追加
  const [submittedEmail, setSubmittedEmail] = useState("");

  // ====== 固定先（ここを必要に応じて変えるだけ） ======
  const FULL_ENDPOINT_URL =
    "https://cyberrangeautomation1.azurewebsites.net/api/blueprint_provision_account?";
  // ===============================================

  const normalize = (s) => (s || "").trim().toLowerCase();
  const validate = (email) => {
    if (!email) return "Please enter your Skool email address.";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) return "Please enter a valid email address.";
    return null;
  };

  async function run(url, init) {
    const res = await fetch(url, init);
    let text = "";
    try {
      text = await res.text();
    } catch {}
    let json = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {}
    }
    return { res, status: res.status, ok: res.ok, text, json };
  }

  async function tryFullUrl(email) {
    // --- POST ---
    {
      const u = new URL(FULL_ENDPOINT_URL);
      const r = await run(u.toString(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        mode: "cors",
        cache: "no-store",
      });
      if (r.ok) return r;
      // 405/404/410 は GET で再試行（※ 410 は churned ケースだが、まずはGETでも確認）
      if (![405, 404, 410].includes(r.status)) return r;
    }

    // --- GET ---
    {
      const u = new URL(FULL_ENDPOINT_URL);
      u.searchParams.set("email", email);
      return await run(u.toString(), {
        method: "GET",
        mode: "cors",
        cache: "no-store",
      });
    }
  }

  async function submit(rawEmail) {
    const email = normalize(rawEmail);
    setSubmittedEmail(email);

    const v = validate(email);
    if (v) return { ok: false, validationMessage: v };

    if (!FULL_ENDPOINT_URL) {
      setStatus(500);
      setMessage("FULL_ENDPOINT_URL is not set.");
      setError("ConfigError");
      return { ok: false, status: 500, message: "FULL_ENDPOINT_URL is not set.", error: "ConfigError" };
    }

    setLoading(true);
    setStatus(null);
    setMessage(null);
    setError(null);

    try {
      const r = await tryFullUrl(email);
      const s = r.status;

      const msg =
        (r.json && (r.json.message ?? r.json.detail ?? r.json.error)) ||
        (r.text && r.text.trim()) ||
        (r.ok ? "Success." : `HTTP ${s} ${r.res?.statusText || ""}`);

      const err =
        (r.json && (r.json.error ?? r.json.code ?? null)) || null;

      setStatus(s);
      setMessage(msg);
      setError(err);

      return {
        ok: r.ok,
        status: s,
        message: msg,
        error: err,
        validationMessage: null,
      };
    } catch (e) {
      const msg = e?.message || "Unknown error.";
      setStatus(500);
      setMessage(msg);
      setError("ClientError");
      return { ok: false, status: 500, message: msg, error: "ClientError" };
    } finally {
      setLoading(false);
    }
  }

  return { submit, loading, status, message, error, submittedEmail };
}
